import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  Button,
  Card,
  Chip,
  PageHeaderLayout,
  TextField,
  Typography,
} from '@/ui';

import type { ChangeEvent } from 'react';

import {
  useGetTypeformAdminSubmissionQuery,
  useLinkTypeformAdminSubmissionMutation,
  useUnlinkTypeformAdminSubmissionMutation,
  useUpdateTypeformAdminSubmissionStatusMutation,
} from '@/store/api/typeformAdminApi';
import { useGetUsersQuery } from '@/store/api/userApi';

const answerValue = (answer: Record<string, unknown>) => {
  if (typeof answer.text === 'string') return answer.text;
  if (typeof answer.email === 'string') return answer.email;
  if (typeof answer.number === 'number') return String(answer.number);
  if (typeof answer.boolean === 'boolean') return answer.boolean ? 'Yes' : 'No';
  if (typeof answer.date === 'string') return answer.date;
  if (typeof answer.choice === 'object' && answer.choice) {
    return String((answer.choice as { label?: string }).label ?? '');
  }
  if (typeof answer.choices === 'object' && answer.choices) {
    const labels = (answer.choices as { labels?: string[] }).labels ?? [];
    return labels.join(', ');
  }

  return JSON.stringify(answer);
};

const TypeformSubmissionDetailPage = () => {
  const { submissionId = '' } = useParams();
  const [userSearch, setUserSearch] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [notice, setNotice] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { data: submission, isLoading } =
    useGetTypeformAdminSubmissionQuery(submissionId);
  const userQueryArgs =
    userSearch.trim().length >= 2
      ? { limit: 10, page: 1, search: userSearch.trim() }
      : skipToken;
  const { data: usersData, isFetching: isSearchingUsers } =
    useGetUsersQuery(userQueryArgs);
  const [linkSubmission, { isLoading: isLinking }] =
    useLinkTypeformAdminSubmissionMutation();
  const [unlinkSubmission, { isLoading: isUnlinking }] =
    useUnlinkTypeformAdminSubmissionMutation();
  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateTypeformAdminSubmissionStatusMutation();

  const userOptions = useMemo(() => usersData?.items ?? [], [usersData]);

  if (isLoading || !submission) {
    return (
      <div className="container mx-auto max-w-screen-xl p-4">
        <Typography variant="body1">Loading submission…</Typography>
      </div>
    );
  }

  const handleLink = async () => {
    try {
      if (!selectedPersonId) {
        setErrorMessage('Select a person before linking.');
        return;
      }

      setErrorMessage('');
      await linkSubmission({
        id: submission.id,
        personId: selectedPersonId,
      }).unwrap();
      setNotice('Submission linked.');
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not link this submission.');
    }
  };

  const handleUnlink = async () => {
    try {
      setErrorMessage('');
      await unlinkSubmission({ id: submission.id }).unwrap();
      setNotice('Submission unlinked.');
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not unlink this submission.');
    }
  };

  const handleStatusToggle = async () => {
    try {
      setErrorMessage('');
      await updateStatus({
        id: submission.id,
        status: submission.status === 'archived' ? 'active' : 'archived',
      }).unwrap();
      setNotice(
        submission.status === 'archived'
          ? 'Submission restored.'
          : 'Submission archived.',
      );
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not update the submission status.');
    }
  };

  return (
    <div className="container mx-auto max-w-screen-xl p-4">
      <PageHeaderLayout
        subtitle={submission.respondentEmail ?? 'No mapped email'}
        title="Typeform Interpretation"
      >
        <Link
          className="no-underline"
          to="/coach/manage/typeform?tab=submissions"
        >
          <Button variant="outlined">Back to submissions</Button>
        </Link>
      </PageHeaderLayout>

      {notice ? (
        <div className="mt-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          {notice}
        </div>
      ) : null}
      {errorMessage ? (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Typography variant="h5">
              {[submission.respondentFirstName, submission.respondentLastName]
                .filter(Boolean)
                .join(' ') || 'Unnamed respondent'}
            </Typography>
            <Chip color="default" label={submission.formType} />
            <Chip
              color={submission.status === 'archived' ? 'warning' : 'success'}
              label={submission.status}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Typography variant="subtitle2">Email</Typography>
              <Typography variant="body2">
                {submission.respondentEmail ?? 'No mapped email'}
              </Typography>
            </div>
            <div>
              <Typography variant="subtitle2">Company</Typography>
              <Typography variant="body2">
                {submission.respondentCompany ?? 'No mapped company'}
              </Typography>
            </div>
            <div>
              <Typography variant="subtitle2">Submitted</Typography>
              <Typography variant="body2">
                {new Date(submission.submittedAt).toLocaleString()}
              </Typography>
            </div>
            <div>
              <Typography variant="subtitle2">Linked person</Typography>
              <Typography variant="body2">
                {submission.linkedPersonId ?? 'Not linked'}
              </Typography>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {submission.pulseReport ? (
              <Link
                className="no-underline"
                to={`/coach/manage/typeform/pulse-reports/${submission.pulseReport.id}`}
              >
                <Button>Manage pulse report</Button>
              </Link>
            ) : (
              <>
                <Button
                  disabled={isUpdatingStatus}
                  onClick={() => {
                    handleStatusToggle();
                  }}
                >
                  {submission.status === 'archived' ? 'Restore' : 'Archive'}
                </Button>
                <Button
                  disabled={!submission.linkedPersonId || isUnlinking}
                  variant="outlined"
                  onClick={() => {
                    handleUnlink();
                  }}
                >
                  Unlink
                </Button>
              </>
            )}
          </div>
          {submission.pulseReport ? (
            <Typography className="mt-4" variant="body2">
              This submission is attached to a pulse report. Visibility and source changes now live in the pulse report workflow.
            </Typography>
          ) : null}
        </Card>

        <Card className="p-4">
          <Typography variant="h5">Link to a person</Typography>
          {submission.pulseReport ? (
            <div className="mt-4 flex flex-col gap-3">
              <Typography variant="body2">
                This raw submission is already attached to a logical pulse report,
                so member linkage must be managed from the pulse report workflow.
              </Typography>
              <Link
                className="no-underline"
                to={`/coach/manage/typeform/pulse-reports/${submission.pulseReport.id}`}
              >
                <Button variant="outlined">Open pulse report</Button>
              </Link>
            </div>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              <TextField
                label="Search users"
                value={userSearch}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setUserSearch(event.target.value);
                }}
              />
              <Typography variant="body2">Matching users</Typography>
              <select
                aria-label="Matching users"
                className="rounded border border-sage-300 p-2"
                id="typeform-person-select"
                value={selectedPersonId}
                onChange={(event) => setSelectedPersonId(event.target.value)}
              >
                <option value="">Select a user</option>
                {userOptions.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.fullName} ({user.email})
                  </option>
                ))}
              </select>
              {isSearchingUsers ? (
                <Typography variant="body2">Searching users…</Typography>
              ) : null}
              <Button
                disabled={isLinking}
                onClick={() => {
                  handleLink();
                }}
              >
                Link selected user
              </Button>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card className="p-4">
          <Typography variant="h5">Answers</Typography>
          <div className="mt-4 flex flex-col gap-3">
            {submission.answers.length ? (
              submission.answers.map((answer, index) => {
                const answerRecord = answer as Record<string, unknown>;
                const ref =
                  (answerRecord.field as { ref?: string } | undefined)?.ref ??
                  `answer-${index}`;
                const key = `${ref}-${String(answerRecord.type ?? 'unknown')}-${answerValue(answerRecord)}`;

                return (
                  <div key={key} className="rounded border border-sage-200 p-3">
                    <Typography variant="subtitle2">{ref}</Typography>
                    <Typography variant="body2">
                      {String(answerRecord.type ?? 'unknown')}
                    </Typography>
                    <Typography className="mt-2" variant="body2">
                      {answerValue(answerRecord)}
                    </Typography>
                  </div>
                );
              })
            ) : (
              <Typography variant="body2">No answers captured.</Typography>
            )}
          </div>
        </Card>

        <Card className="p-4">
          <Typography variant="h5">Raw payload</Typography>
          <div className="mt-4 flex flex-col gap-4">
            <div>
              <Typography variant="subtitle2">Definition</Typography>
              <pre className="bg-sage-50 mt-2 overflow-auto rounded p-3 text-xs">
                {JSON.stringify(submission.definition ?? {}, null, 2)}
              </pre>
            </div>
            <div>
              <Typography variant="subtitle2">Hidden</Typography>
              <pre className="bg-sage-50 mt-2 overflow-auto rounded p-3 text-xs">
                {JSON.stringify(submission.hidden ?? {}, null, 2)}
              </pre>
            </div>
            <div>
              <Typography variant="subtitle2">Calculated</Typography>
              <pre className="bg-sage-50 mt-2 overflow-auto rounded p-3 text-xs">
                {JSON.stringify(submission.calculated ?? {}, null, 2)}
              </pre>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TypeformSubmissionDetailPage;
